/**
 * useSortableList — shared list-sorting hook.
 *
 * The customer & admin list pages all repeat the same pattern:
 *   • Track `{key, dir}` state.
 *   • On column click, toggle direction if same key, else set new key
 *     with a column-appropriate default direction.
 *   • Sort a source array with a per-key comparator, sinking
 *     null/undefined values to the bottom regardless of direction so
 *     users don't hit a wall of blank rows.
 *
 * `comparators` shape: `{ key: (item) => sortableValue }`. Return a
 * string for lexicographic sort (uses `localeCompare` with numeric
 * option — natural for invoice numbers), or a number/date for numeric.
 *
 * `defaultDescKeys` lists keys that should default to descending on
 * first click (dates + amounts — "newest / largest first" is what
 * users usually want when they click a fresh column).
 */
import { useMemo, useState, useCallback } from 'react';

export const useSortableList = (items, comparators, {
  initialKey,
  initialDir = 'desc',
  defaultDescKeys = [],
} = {}) => {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });

  const onSort = useCallback((key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: defaultDescKeys.includes(key) ? 'desc' : 'asc' });
  }, [defaultDescKeys]);

  const sorted = useMemo(() => {
    const getVal = comparators[sort.key];
    if (!getVal) return items;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const arr = [...items];
    arr.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      // Nulls / empty strings always sink to the bottom.
      const aEmpty = va === null || va === undefined || va === '';
      const bEmpty = vb === null || vb === undefined || vb === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * dir;
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [items, sort, comparators]);

  return { sorted, sort, onSort };
};

export default useSortableList;
